import React from 'react';
import { Box, Checkbox, Chip, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { FileTypePill } from '../atoms';
import type { IllustrationFileType } from '../DocumentIllustration';
import { StatusPill, type ContentStatus } from './StatusPill';
import type { UnifiedTag } from '../../../services/content/tag.service';

export interface UploadFileRowProps {
  fileId: string;
  fileName: string;
  fileSize: number; // bytes
  fileType: IllustrationFileType;
  status: ContentStatus;
  tags: string[];
  /** Resolved tag list, used to look up name/color for each tag id in `tags`. */
  availableTags: UnifiedTag[];
  /** Whether the title slot shows the AI-pending state (italic + chip). */
  aiTitle: boolean;
  /** Whether AI is filling description (so we hide the "+ add description" link). */
  aiDescription: boolean;
  /** Title text the user has typed (used in Pass 4). Empty string means default to filename. */
  title?: string;
  description?: string;
  selected: boolean;
  onSelectChange: (next: boolean) => void;
  onStatusClick: (anchor: HTMLElement) => void;
  onAddTagClick: (anchor: HTMLElement) => void;
  onRemove: () => void;
  /** Validation: row is in error state (e.g. required title empty). */
  errored?: boolean;
  /** Called when the user commits a new title (blur or Enter). Pass '' to clear. */
  onTitleChange?: (next: string) => void;
  onDescriptionChange?: (next: string) => void;
}

const formatBytes = (b: number): string => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

export const UploadFileRow: React.FC<UploadFileRowProps> = ({
  fileName,
  fileSize,
  fileType,
  status,
  tags,
  availableTags,
  aiTitle,
  aiDescription,
  title,
  description,
  selected,
  onSelectChange,
  onStatusClick,
  onAddTagClick,
  onRemove,
  errored,
  onTitleChange,
  onDescriptionChange,
}) => {
  const tagsById = React.useMemo(() => {
    const m = new Map<string, UnifiedTag>();
    availableTags.forEach((t) => m.set(t.id, t));
    return m;
  }, [availableTags]);

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 2.25,
        py: 1.75,
        borderBottom: `1px solid ${theme.palette.divider}`,
        transition: 'background 0.1s',
        '&:hover': { background: theme.palette.action.hover },
        ...(selected && { background: 'rgba(34,181,220,.06)' }),
        ...(errored && {
          background: 'rgba(220,38,38,.04)',
          borderLeft: '3px solid #DC2626',
          pl: 2,
        }),
      })}
    >
      <Checkbox
        size="small"
        checked={selected}
        onChange={(e) => onSelectChange(e.target.checked)}
        sx={{ p: 0.5, mt: 0.25 }}
        inputProps={{ 'aria-label': `Select ${fileName}` }}
      />
      <FileTypePill fileType={fileType} variant="inline" />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {aiTitle ? (
            <>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: 13.5,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {title || fileName}
              </Typography>
              <Chip
                size="small"
                icon={<AutoAwesomeIcon sx={{ fontSize: 11 }} />}
                label="Title"
                sx={(theme) => ({
                  fontSize: 10,
                  fontWeight: 700,
                  height: 18,
                  backgroundColor: 'rgba(34,181,220,.13)',
                  color: theme.palette.brand.teal,
                  '& .MuiChip-icon': { ml: 0.5, mr: -0.5 },
                })}
              />
            </>
          ) : (
            <InlineEditableTitle
              value={title ?? ''}
              placeholder={fileName}
              onCommit={(v) => onTitleChange?.(v)}
            />
          )}
        </Box>
        {!aiDescription && (
          <InlineExpandableDescription
            value={description ?? ''}
            onCommit={(v) => onDescriptionChange?.(v)}
          />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', fontSize: 12, color: 'text.disabled' }}>
          <span>{formatBytes(fileSize)}</span>
          {tags.map((tagId) => {
            const tag = tagsById.get(tagId);
            const label = tag?.name ?? tagId;
            const bg = tag?.color ? `${tag.color}20` : 'rgba(34,181,220,.13)';
            const fg = tag?.color ?? undefined;
            return (
              <Chip
                key={tagId}
                size="small"
                label={label}
                sx={(theme) => ({
                  height: 20,
                  fontSize: 11,
                  fontWeight: 600,
                  backgroundColor: bg,
                  color: fg ?? theme.palette.brand.teal,
                  borderColor: fg,
                })}
                variant={fg ? 'outlined' : 'filled'}
              />
            );
          })}
          <Box
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onAddTagClick(e.currentTarget); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onAddTagClick(e.currentTarget as HTMLElement);
              }
            }}
            sx={(theme) => ({
              cursor: 'pointer',
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: 1,
              px: 1,
              py: 0.125,
              fontSize: 11,
              color: 'text.disabled',
              '&:hover': { borderColor: theme.palette.brand.cyan, color: theme.palette.brand.teal },
            })}
          >
            + tag
          </Box>
          {errored && (
            <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
              ⚠︎ Title required
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, flexShrink: 0 }}>
        <StatusPill status={status} onClick={(e) => onStatusClick(e.currentTarget)} />
      </Box>
      <IconButton size="small" onClick={onRemove} aria-label={`Remove ${fileName}`}>
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
};

const InlineEditableTitle: React.FC<{ value: string; onCommit: (v: string) => void; placeholder: string }> = ({ value, onCommit, placeholder }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value);
  const escapeRef = React.useRef(false);

  // Keep the local draft in sync if the parent value changes while not editing
  // (e.g. AI toggle flipping or an upstream reset).
  React.useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  if (!editing) {
    return (
      <Box
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setEditing(true);
          }
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'text',
          minWidth: 0,
          '&:hover .upload-row-pencil': { opacity: 1 },
        }}
      >
        {value ? (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: 13.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {value}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: 13.5,
              fontStyle: 'italic',
              color: 'text.disabled',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {placeholder}
          </Typography>
        )}
        <Box className="upload-row-pencil" sx={{ color: 'text.disabled', fontSize: 11, opacity: 0, transition: 'opacity .1s' }}>
          ✎
        </Box>
      </Box>
    );
  }

  return (
    <TextField
      size="small"
      autoFocus
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (escapeRef.current) { escapeRef.current = false; setEditing(false); return; }
        onCommit(draft);
        setEditing(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          escapeRef.current = true;
          setDraft(value);
          (e.target as HTMLInputElement).blur();
        }
      }}
      sx={{ '& .MuiInputBase-input': { fontSize: 13.5, fontWeight: 600, py: 0.5 } }}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const InlineExpandableDescription: React.FC<{ value: string; onCommit: (v: string) => void }> = ({ value, onCommit }) => {
  const [expanded, setExpanded] = React.useState<boolean>(Boolean(value));
  const [draft, setDraft] = React.useState(value);

  React.useEffect(() => {
    if (!expanded) setDraft(value);
  }, [value, expanded]);

  if (!expanded) {
    return (
      <Typography
        component="button"
        type="button"
        variant="caption"
        onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
        sx={(theme) => ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          color: 'text.disabled',
          cursor: 'pointer',
          alignSelf: 'flex-start',
          background: 'transparent',
          border: 'none',
          padding: 0,
          font: 'inherit',
          '&:hover': { color: theme.palette.brand.teal },
          '&::before': { content: '"+"', fontWeight: 700, mr: 0.25 },
        })}
      >
        add description
      </Typography>
    );
  }

  return (
    <TextField
      size="small"
      multiline
      minRows={2}
      maxRows={5}
      autoFocus={!draft}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onBlur={() => {
        onCommit(draft);
        if (!draft.trim()) setExpanded(false);
      }}
      placeholder="Describe this file…"
      sx={{ '& .MuiInputBase-input': { fontSize: 12.5 }, mt: 0.25 }}
    />
  );
};
